import { makeAutoObservable } from "mobx"
import { deferAction } from "reusable/utils/utils"
import { Queue } from "./Queue"
import { TRPCClientError } from "@trpc/client"

export type JobQueueFunc<T = any> = () => Promise<T>
type JobQueueElement = { func: JobQueueFunc, resolve: (value: any) => any, debugName?: string }

interface JobQueueProps {
   onStartedRunning?: () => any
   onFinishedRunning?: () => any
   debug?: boolean
   dontThrowErrorOnNetworkFailure?: boolean
}

export class JobQueue {
   public promise: Promise<any> | null = null
   private sp
   private queue = new Queue<JobQueueElement>()
   private running = false
   /** Resolvers waiting on `idle()` — settled the next time the queue drains. */
   private drainResolvers: Array<() => void> = []

   constructor(sp?: JobQueueProps) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get isRunning() {
      return this.running
   }

   /**
   * Resolve once the queue has finished draining (no more queued jobs and
   * the engine isn't running). Returns a resolved promise immediately if the
   * queue is already idle. Use this before reading state that downstream
   * code expects to be post-flush — e.g. cleanup-on-close evaluates a server
   * predicate that must see the latest debounced title save.
   */
   idle = async (): Promise<void> => {
      if (!this.running && this.queue.isEmpty) return
      // Wait for the next `finishRunning()` rather than polling `this.promise`.
      // Polling could spin forever if a job left a settled-rejected promise in
      // place while `running` stayed true; this resolves deterministically when
      // the engine finishes draining (or recovers from a failed job).
      await new Promise<void>(resolve => {
         this.drainResolvers.push(resolve)
      })
   }

   /**
   * Add a job to the queue.
   * If `replaceLastInQueue` is true, the last job in the queue will be replaced.
   */
   run = <T>(func: JobQueueFunc<T>, debugName?: string, replaceLastInQueue = false): Promise<T> => {
      return new Promise<T>((resolve) => {
         if (replaceLastInQueue && this.queue.length > 1) {
            this.debug("Replacing the last job in the queue...", debugName)
            this.queue.replaceLast({ func, resolve, debugName })
         } else {
            this.debug("Job enqueued...", debugName)
            this.queue.enqueue({ func, resolve, debugName })
         }

         if (!this.running) {
            this.startEngine()
         }
      })
   }

   private debug = (msg: string, debugName?: string) => {
      if (this.sp?.debug) {
         console.log(`${(debugName ? `[${debugName}] ` : "")}Queue: ${msg}`)
      }
   }

   private startRunning = () => {
      if (!this.running) {
         this.debug("Queue started running...")
         this.sp?.onStartedRunning?.()
         this.running = true
      }
   }

   private finishRunning = () => {
      if (this.running) {
         this.debug("Queue finished running...")
         this.sp?.onFinishedRunning?.()
         this.running = false
      }
      this.promise = null
      // Wake any `idle()` waiters now the queue has drained.
      if (this.drainResolvers.length > 0) {
         const resolvers = this.drainResolvers
         this.drainResolvers = []
         resolvers.forEach(resolve => resolve())
      }
   }

   private startEngine = async () => {
      this.startRunning()
      deferAction(async () => {
         try {
            while (!this.queue.isEmpty) {
               const job = this.queue.peek()
               try {
                  this.promise = this.resolveJob(job)
                  const result = await this.promise
                  if (result) {
                     // Only remove job from queue if it succeeded.
                     this.queue.dequeue()
                  } else {
                     // This happens on network failure. await 5 seconds and try again. Job stays in queue
                     await new Promise(f => setTimeout(f, 5000))
                  }
               } catch {
                  // Non-retryable failure: `resolveJob` already settled the
                  // caller's promise (null). Drop the job so the engine drains
                  // the rest of the queue instead of spinning on it.
                  this.queue.dequeue()
               }
            }
         } finally {
            // Always release the running flag and wake `idle()` waiters, even if
            // the loop exits via an unexpected throw. Without this a failed job
            // left `running` stuck true and `idle()` hung forever.
            deferAction(() => {
               this.finishRunning()
            })
         }
      })
   }

   private resolveJob = async (job: JobQueueElement): Promise<boolean> => {
      this.debug("Job running...", job.debugName)
      try {
         const value = await job.func()
         this.debug("Job finished running...", job.debugName)
         job.resolve(value)
         return true
      } catch (error: any) {
         const isNetworkFailure = error.name === 'TRPCClientError' && (error as TRPCClientError<any>).message.startsWith('Failed to fetch')
         if (isNetworkFailure && this.sp?.dontThrowErrorOnNetworkFailure === true) {
            // Retryable: leave the job queued and signal the engine to wait + retry.
            this.debug("Job failed due to network connectivity issues", job.debugName)
            return false
         }
         // Non-retryable (or a network failure on a queue not configured to
         // retry): resolve the caller with null so its promise never leaks,
         // then rethrow so the engine drops the job and moves on.
         this.debug("Job failed", job.debugName)
         job.resolve(null)
         throw error
      }
   }
}
